import {
    LOAD_PLAYLIST,
    SET_PLAYLIST_ITEM_COMPLETED,
    SET_RAW_PLAYLISTS_BY_PERIOD,
    SET_DATA_IS_FETCHED
} from './playlistActions';
import {
    ASSESSMENT_MODALITY,
    ASSESSMENT_TYPE,
    LC,
    MEASURES_CONFIG,
    CLPL,
} from '../../constants/measure';
import { LANGUAGE } from '../../constants/language';
import { STATUS, PERIOD } from '../../constants/result';
import CLPL_FORM_NUMBER from '../../constants/queryParam';
import PLAYLIST_SUPPORTED_ITEMS from '../../constants/playlistItems';
import { SET_FORM } from '../api/formsActions';
import { SET_LC_RESULT, SET_RESULT } from '../api/resultActions';
import { FINAL_LC_RESULT_INDEX } from '../../lc/lcConstants';
import { getURLParam } from '../utils/browserUtils';

const FIRST_PLAYLIST_INDEX = 0;
const ENROLLMENT_ID_KEY = 'student_enrollment_id';
const INST_UID_KEY = 'inst_uid';

export const defaultState = {
    isDataFetched: false,
    assessmentType: undefined,
    schoolYear: undefined,
    measuresConfig: {},
    items: [],
    toy: undefined,
    grade: undefined,
    rawPlaylistsByPeriod: {},
    measureInstIds: {},
    instTemporalIds: {},
};

const shouldUpdateItem = (item, result) => {
    return (item.measure === result.measure &&
        (result.measure_type ? item.measureType === result.measure_type : true) &&
        (result.language ? item.language === result.language : true));
};

const itemMatchParams = (item, { measureKey, language = LANGUAGE.ENG }) => {
    return item.language === language &&
            (item.key === measureKey ||
            item.key === `${measureKey}-${language}`);
};

const getInstTemporalIds = (onlineItemsDict) => {
    const instTemporalIds = {};
    Object.values(onlineItemsDict).forEach((item) => {
        if (instTemporalIds[item[INST_UID_KEY]] === undefined) {
            instTemporalIds[item[INST_UID_KEY]] = item[ENROLLMENT_ID_KEY];
        }
    });
    return instTemporalIds;
};

const getInstIdsFromMeasures = (onlineItemsDict) => {
    const measureInstIds = {};
    Object.values(onlineItemsDict).forEach((item) => {
        const measureItem = PLAYLIST_SUPPORTED_ITEMS.find(supportedItem => item.measure === supportedItem.type);
        if (measureItem) measureInstIds[measureItem.measure] = item[INST_UID_KEY];
    });
    return measureInstIds;
};

const createPlaylist = (rawPlaylists) => {
    let onlineItemsDict = {};
    let measureInstIds = {};
    let instTemporalIds = {};
    const { period, grade } = rawPlaylists[FIRST_PLAYLIST_INDEX];

    const allPlaylistItems = rawPlaylists.reduce((playlistItems, playlist) => {
        if (playlist.playlist_items) return [...playlistItems, ...playlist.playlist_items];
        return playlistItems;
    }, []);

    if (allPlaylistItems) {
        onlineItemsDict = allPlaylistItems.reduce((resultDict, item) => {
            const newResultDict = { ...resultDict };
            if (item.modality === ASSESSMENT_MODALITY.ONLINE) {
                newResultDict[item.measure] = item;
            }
            return newResultDict;
        }, {});
        instTemporalIds = getInstTemporalIds(allPlaylistItems);
        measureInstIds = getInstIdsFromMeasures(onlineItemsDict);
    }

    const items = PLAYLIST_SUPPORTED_ITEMS.filter(item => onlineItemsDict[item.type] !== undefined).map(item => ({
        ...item,
        ...(onlineItemsDict[item.type].secure ? {secure: true} : {})
    }));

    const {
        assessment_type: assessmentType,
        school_year: schoolYear,
        measures_config: measuresConfig
    } = rawPlaylists[FIRST_PLAYLIST_INDEX];

    return {
        isDataFetched: true,
        toy: period,
        grade,
        assessmentType,
        schoolYear,
        measuresConfig: {
            ...( measuresConfig ? { [MEASURES_CONFIG.RCT_FORM]: measuresConfig.rct_form } : {}),
        },
        items,
        instTemporalIds,
        measureInstIds,
    };
};

// eslint-disable-next-line consistent-return
const updateItemStatus = (resultIndex, result, item) => {
    if (resultIndex < FINAL_LC_RESULT_INDEX) {
        return {
            ...item,
            status: !result.is_complete ? STATUS.IN_PROGRESS : item.status
        };
    }
    if (resultIndex === FINAL_LC_RESULT_INDEX) {
        return {
            ...item,
            status: result.is_complete ? STATUS.COMPLETED : STATUS.IN_PROGRESS,
        };
    }
};

const groupByToy = (playlists) => {
    const playlistsByToy = {};
    // We use this filtering for force displaying of PM CLPL assesment in case there is any other one in playlist,
    // but user added special parameter to url
    if(getURLParam(CLPL_FORM_NUMBER)) {
        const clplPMPlaylist = playlists.filter(playlist => playlist.period === PERIOD.PM &&
        playlist.playlist_items.find(playlistItem => playlistItem.measure === CLPL.MEASURE));
        return {
            PM: clplPMPlaylist
        };
    }
    playlists.forEach((playlist) => {
        const { period } = playlist;
        if (period in playlistsByToy) playlistsByToy[period].push(playlist);
        else playlistsByToy[period] = [playlist];
    });
    return playlistsByToy;
};

const playlistReducer = (state = defaultState, action) => {
    switch (action.type) {
    case SET_RAW_PLAYLISTS_BY_PERIOD:
        return {
            ...state,
            rawPlaylistsByPeriod: groupByToy(action.payload.playlists)
        };
    case LOAD_PLAYLIST:
        return {
            ...state,
            ...createPlaylist(action.payload.playlists),
        };
    case SET_PLAYLIST_ITEM_COMPLETED:
        return {
            ...state,
            items: state.items.map((item) => {
                const isMatch = itemMatchParams(item, action);
                return {
                    ...item,
                    isRecentlyCompleted: isMatch,
                    status: isMatch ? STATUS.COMPLETED : item.status,
                };
            }),
        };
    case SET_RESULT:
        return {
            ...state,
            items: state.items.map((item) => {
                if (shouldUpdateItem(item, action.result)) {
                    return {
                        ...item,
                        status: action.result.is_complete ? STATUS.COMPLETED : STATUS.IN_PROGRESS,
                    };
                }
                return item;
            }),
        };
    case SET_FORM:
        return {
            ...state,
            items: state.items.map((item) => {
                if (state.assessmentType === ASSESSMENT_TYPE.PM
                    && item.measure === action.form.measure
                    && item.measureType === action.form.measure_type) {
                    return {
                        ...item,
                        status: STATUS.NOT_STARTED,
                    };
                }
                return item;
            }),
        };
    case SET_LC_RESULT:
    {
        const { result, resultIndex } = action;
        const isLCMeasure = (resultParam, item) => resultParam.measure === LC.MEASURE && item.measure === resultParam.measure;
        return {
            ...state,
            items: state.items.map((item) => {
                if (isLCMeasure(result, item)) return updateItemStatus(resultIndex, result, item);
                return item;
            }),
        };
    }
    case SET_DATA_IS_FETCHED:
        return {
            ...state,
            isDataFetched: true,
        };
    default:
        return state;
    }
};

export default playlistReducer;
